import {useState, useEffect} from 'react'
import {Cable, Power, PowerOff, TestTube2, Trash2} from 'lucide-react'
import {Stack} from '@/components/shared/Stack'
import {useNavigate, useParams} from 'react-router-dom'
import {useI18n} from '@/i18n'
import {Button} from '@/components/ui/button'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {useAuth} from '@/contexts/auth-context'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {OutboundConnectorType} from '@/types/outbound-connector'
import type {OutboundConnectorTestResult} from '@/types/outbound-connector'
import {permissions} from '@/lib/permissions'
import {useOutboundConnector, useOutboundConnectorLogs, useUpdateConnector, useDeleteConnector, useTestConnector} from '@/hooks/use-outbound-connectors'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {PartyResponseFields} from '@/types/party'
import {PageLayout} from '@/components/shared/PageLayout'
import {InlineRow} from '@/components/shared/InlineRow'
import {Icon} from '@/components/shared/Icon'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'

import {ConnectorOverviewCard} from './components/ConnectorOverviewCard'
import {ConnectorLogView} from './components/ConnectorLogView'
import {ConnectorTestDialog} from './components/ConnectorTestDialog'
import {ConnectorAttributesTab} from './components/ConnectorAttributesTab'
import {ScimConfigCard} from './components/forms/ScimConfigCard'
import {LdapConfigCard} from './components/forms/LdapConfigCard'
import {RestConfigCard} from './components/forms/RestConfigCard'
import {DatabaseConfigCard} from './components/forms/DatabaseConfigCard'
import {K8sConfigCard} from './components/forms/K8sConfigCard'
import {EmailConfigCard} from './components/forms/EmailConfigCard'
import {ScriptConfigCard} from './components/forms/ScriptConfigCard'
import {SapAbapConfigCard} from './components/forms/SapAbapConfigCard'
import {OutboundConnectorAttributeMappingCard} from './components/AttributeMappingCard'
import {LdapProvisioningConfigCard} from './components/LdapProvisioningConfigCard'
import {ProvisioningTasksSubPage} from './sub-pages/ProvisioningTasksSubPage'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {useDialog} from '@/hooks/useDialog'

export function OutboundConnector() {
    const i18n = useI18n()
    const navigate = useNavigate()
    const {id} = useParams<{id: string}>()
    const {hasPermission} = useAuth()
    const {connectorTypes} = useAppConfig()

    const canUpdate = hasPermission(permissions.outboundConnectors.update)
    const canDelete = hasPermission(permissions.outboundConnectors.delete)
    const canManageAttributes = hasPermission(permissions.outboundConnectors.attributes.manage)

    const [testResult, setTestResult] = useState<OutboundConnectorTestResult | null>(null)
    const testResultDialog = useDialog()
    const deleteDialog = useDialog()

    // Dual-editor dirty state
    const [attributesDirty, setAttributesDirty] = useState(false)
    const [ldapConfigDirty, setLdapConfigDirty] = useState(false)
    const eitherDirty = attributesDirty || ldapConfigDirty

    // Navigation guard state (manual pattern — BrowserRouter does not support useBlocker)
    const navGuardDialog = useDialog()
    const [pendingNavTarget, setPendingNavTarget] = useState<string | null>(null)

    const guardedNavigate = (target: string | number) => {
        if (eitherDirty) {
            setPendingNavTarget(typeof target === 'string' ? target : null)
            navGuardDialog.open()
        } else if (typeof target === 'string') {
            navigate(target)
        } else {
            navigate(target)
        }
    }

    // beforeunload listener — fires on browser refresh / tab close when dirty
    useEffect(() => {
        if (!eitherDirty) return
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault()
            e.returnValue = ''
        }
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [eitherDirty])

    // popstate listener — fires on browser Back button when dirty
    const openNavGuard = navGuardDialog.open
    useEffect(() => {
        if (!eitherDirty) return
        const handler = (e: PopStateEvent) => {
            e.preventDefault()
            // Push current state back so we can intercept
            window.history.pushState(null, '', window.location.href)
            openNavGuard()
            setPendingNavTarget(null)
        }
        window.addEventListener('popstate', handler)
        return () => window.removeEventListener('popstate', handler)
    }, [eitherDirty, openNavGuard])

    const {data: connector, isLoading, error} = useOutboundConnector(id)

    const {data: logsData, refetch: refetchLogs} = useOutboundConnectorLogs(id)
    const logs = logsData?.content ?? []

    const toggleMutation = useUpdateConnector(id!)

    const testMutation = useTestConnector(id!)
    const deleteMutation = useDeleteConnector()

    const handleDelete = () => {
        if (!id) return
        deleteMutation.mutate(id, {
            onSuccess: () => {
                deleteDialog.close()
                navigate(routes.connectors.outbound.list())
            },
        })
    }

    const handleToggleEnabled = () => {
        if (!connector) return
        toggleMutation.mutate({enabled: !connector.enabled, version: connector.version})
    }

    const hasConfig = !!(
        connector?.scimConfig
        || connector?.ldapConfig
        || connector?.restConfig
        || connector?.databaseConfig
        || connector?.k8sConfig
        || connector?.emailConfig
        || connector?.scriptConfig
        || connector?.connectorType === 'SAP_ABAP'
    )

    const connectorTypeLabel = connector ? labelOf(connectorTypes, connector.connectorType) : ''

    const detailError = error ?? (!isLoading ? notFoundIfMissing(connector, 'OUTBOUND_CONNECTOR') : undefined)

    const headerActions = (canUpdate || canDelete) ? (
        <InlineRow>
            {canUpdate && hasConfig && (
                <Button
                    data-testid="btn-test-connection"
                    variant="outline"
                    onClick={() => testMutation.mutate(undefined, {
                        onSuccess: (result: OutboundConnectorTestResult) => {
                            setTestResult(result)
                            testResultDialog.open()
                            refetchLogs()
                        },
                    })}
                    disabled={testMutation.isPending}
                >
                    {testMutation.isPending ? (
                        <ButtonSpinner/>
                    ) : (
                        <Icon icon={TestTube2} className="mr-2"/>
                    )}
                    {i18n.connectors.testConnection}
                </Button>
            )}
            {canUpdate && (
                <Button
                    data-testid="btn-toggle-enabled"
                    variant={connector?.enabled ? 'outline' : 'default'}
                    onClick={handleToggleEnabled}
                    disabled={toggleMutation.isPending}
                >
                    {connector?.enabled ? (
                        <>
                            <Icon icon={PowerOff} className="mr-2"/>
                            {i18n.connectors.disable}
                        </>
                    ) : (
                        <>
                            <Icon icon={Power} className="mr-2"/>
                            {i18n.connectors.enable}
                        </>
                    )}
                </Button>
            )}
            {canDelete && (
                <Button
                    data-testid="btn-delete"
                    variant="destructive"
                    onClick={deleteDialog.open}
                >
                    <Icon icon={Trash2} className="mr-2"/>
                    {i18n.connectors.deleteConnector}
                </Button>
            )}
        </InlineRow>
    ) : undefined

    const headerDescription = connector ? (
        <InlineRow>
            <Badge variant="outline">{connectorTypeLabel}</Badge>
            {connector.enabled ? (
                <Badge variant={BadgeVariant.SUCCESS}>{i18n.common.enabled}</Badge>
            ) : (
                <Badge variant={BadgeVariant.WARNING}>{i18n.common.disabled}</Badge>
            )}
        </InlineRow>
    ) : undefined

    return (
        <PageLayout
            variant="detail"
            data-testid="outbound-connector-detail-page"
            icon={Cable}
            title={connector?.targetSystem.displayName ?? ''}
            description={headerDescription}
            actions={headerActions}
            backTo={true}
            onBack={() => guardedNavigate(-1)}
            loading={isLoading}
            error={detailError}
        >
            {connector && (
                <>
                        <Tabs defaultValue="config">
                            <TabsList>
                                <TabsTrigger value="overview" data-testid="tab-overview">{i18n.connectors.tabs.overview}</TabsTrigger>
                                <TabsTrigger value="config" data-testid="tab-config">{i18n.connectors.tabs.configuration}</TabsTrigger>
                                <TabsTrigger value={PartyResponseFields.attributes} data-testid="tab-attributes">{i18n.connectors.tabs.attributes}</TabsTrigger>
                                {connector.targetSystem.id && (
                                    <TabsTrigger value="provisioning-tasks" data-testid="tab-provisioning-tasks">{i18n.connectors.tabs.provisioningTasks}</TabsTrigger>
                                )}
                            </TabsList>

                            <TabsContent value="overview" className="mt-6">
                                <Stack gap="lg">
                                    <ConnectorOverviewCard connector={connector} connectorTypeLabel={connectorTypeLabel}/>
                                    <ConnectorLogView logs={logs} onRefresh={() => refetchLogs()}/>
                                </Stack>
                            </TabsContent>

                            <TabsContent value="config" className="mt-6">
                                <Stack gap="lg">
                                    {connector.connectorType === OutboundConnectorType.SCIM_2_0 && (
                                        <ScimConfigCard connector={connector} canUpdate={canUpdate}/>
                                    )}

                                    {connector.connectorType === OutboundConnectorType.LDAP && (
                                        <LdapConfigCard connector={connector} canUpdate={canUpdate}/>
                                    )}

                                    {connector.connectorType === OutboundConnectorType.REST && (
                                        <RestConfigCard connector={connector} canUpdate={canUpdate}/>
                                    )}

                                    {connector.connectorType === OutboundConnectorType.DATABASE && (
                                        <DatabaseConfigCard connector={connector} canUpdate={canUpdate}/>
                                    )}

                                    {connector.connectorType === OutboundConnectorType.KUBERNETES && (
                                        <K8sConfigCard connector={connector} canUpdate={canUpdate}/>
                                    )}

                                    {connector.connectorType === OutboundConnectorType.EMAIL && (
                                        <EmailConfigCard connector={connector} canUpdate={canUpdate}/>
                                    )}

                                    {connector.connectorType === OutboundConnectorType.SCRIPT && (
                                        <ScriptConfigCard
                                            connector={connector}
                                            canUpdate={canUpdate}
                                        />
                                    )}

                                    {connector.connectorType === OutboundConnectorType.SAP_ABAP && (
                                        <SapAbapConfigCard connector={connector} canUpdate={canUpdate}/>
                                    )}

                                    <OutboundConnectorAttributeMappingCard
                                        connector={connector}
                                        canUpdate={canUpdate}
                                        onDirtyChange={setAttributesDirty}
                                    />

                                    {connector.connectorType === OutboundConnectorType.LDAP && (
                                        <LdapProvisioningConfigCard
                                            connector={connector}
                                            canUpdate={canUpdate}
                                            onDirtyChange={setLdapConfigDirty}
                                        />
                                    )}
                                </Stack>
                            </TabsContent>

                            <TabsContent value={PartyResponseFields.attributes} className="mt-6">
                                <ConnectorAttributesTab
                                    connectorId={connector.id}
                                    canEdit={canManageAttributes}
                                />
                            </TabsContent>

                            {connector.targetSystem.id && (
                                <TabsContent value="provisioning-tasks" className="mt-6">
                                    <ProvisioningTasksSubPage
                                        connectorId={connector.id}
                                        targetSystemId={connector.targetSystem.id}
                                    />
                                </TabsContent>
                            )}
                        </Tabs>

                        <ConnectorTestDialog
                            open={testResultDialog.isOpen}
                            onClose={testResultDialog.close}
                            testResult={testResult}
                            connectorType={connector.connectorType}
                            testId="test-result-dialog"
                        />

                        <Dialog open={deleteDialog.isOpen} onOpenChange={deleteDialog.bind.onOpenChange}>
                            <DialogContent data-testid="delete-confirm-dialog">
                                <DialogHeader>
                                    <DialogTitle>{i18n.connectors.deleteConnector}</DialogTitle>
                                    <DialogDescription>
                                        {i18n.connectors.deleteConfirm({name: connector.targetSystem.displayName})}
                                    </DialogDescription>
                                </DialogHeader>
                                <SaveCancelFooter
                                    testIdPrefix="delete"
                                    onCancel={deleteDialog.close}
                                    saving={deleteMutation.isPending}
                                    saveLabel={i18n.common.delete}
                                    destructive
                                    saveType="button"
                                    onSave={handleDelete}
                                />
                            </DialogContent>
                        </Dialog>

                        <Dialog open={navGuardDialog.isOpen} onOpenChange={(open) => !open && navGuardDialog.close()}>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>{i18n.connectors.unsavedChangesTitle}</DialogTitle>
                                    <DialogDescription>{i18n.connectors.unsavedChangesBody}</DialogDescription>
                                </DialogHeader>
                                <DialogFooter>
                                    <Button
                                        variant="outline"
                                        onClick={navGuardDialog.close}
                                        data-testid="btn-unsaved-stay"
                                    >
                                        {i18n.connectors.unsavedChangesStay}
                                    </Button>
                                    <Button
                                        variant="destructive"
                                        onClick={() => {
                                            navGuardDialog.close()
                                            setAttributesDirty(false)
                                            setLdapConfigDirty(false)
                                            if (pendingNavTarget !== null) {
                                                navigate(pendingNavTarget)
                                            } else {
                                                navigate(-1)
                                            }
                                        }}
                                        data-testid="btn-unsaved-leave"
                                    >
                                        {i18n.connectors.unsavedChangesLeave}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </>
            )}
        </PageLayout>
    )
}
